import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useFormConfigLoader } from '@/hooks';
import { IOffenseOption, IOffenseOptionItem } from '@/models';

import { getOffenseOptionItems } from '../KaseCrimes/utilities';

interface KaseMainContextProviderProps extends PropsWithChildren {
  refetchCounts?: () => void;
}

interface KaseMainContextValues extends PropsWithChildren {
  PropertyInvolvementList: Map<string, IOffenseOptionItem>;
  DrugType: Map<string, IOffenseOptionItem>;
  drugDataSources: Map<string, IOffenseOptionItem>;
  PropertyType: Map<string, IOffenseOptionItem>;
  filteredOptions: IOffenseOption[];
  refetchCounts?: () => void;
}
const KaseMainContext = createContext<KaseMainContextValues | undefined>(
  undefined,
);

export function useKaseMainContext() {
  const context = useContext(KaseMainContext);
  if (!context) {
    throw new Error(
      'useKaseMainContext must be used within a KaseMainContextProviderProps',
    );
  }
  return context;
}

const emptyKaseMainContext: KaseMainContextValues = {
  PropertyInvolvementList: new Map(),
  DrugType: new Map(),
  drugDataSources: new Map(),
  PropertyType: new Map(),
  filteredOptions: [],
  children: undefined,
};

export function useOptionalKaseMainContext(): KaseMainContextValues {
  const context = useContext(KaseMainContext);
  return context ?? emptyKaseMainContext;
}

export function KaseMainContextProvider({
  children,
  refetchCounts,
}: KaseMainContextProviderProps) {
  const [DrugType, setDrugTypes] = useState<Map<string, IOffenseOptionItem>>();
  const [PropertyType, setPropertyType] =
    useState<Map<string, IOffenseOptionItem>>();
  const [PropertyInvolvementList, setPropertyInvolvementList] =
    useState<Map<string, IOffenseOptionItem>>();
  const [drugDataSources, setDrugDataSources] =
    useState<Map<string, IOffenseOptionItem>>();
  const { filteredOptions } = useFormConfigLoader({
    optionsName: 'PROPERTY',
    category: ['DRUGS', 'GENERAL', 'PROPERTY'],
  });

  useEffect(() => {
    if (filteredOptions) {
      const propertyTypeOpionts = getOffenseOptionItems(
        'propertyType',
        filteredOptions,
      ) as Map<string, IOffenseOptionItem>;

      setPropertyType(propertyTypeOpionts);
      const drugTypesOptions = getOffenseOptionItems(
        'type',
        filteredOptions,
      ) as Map<string, IOffenseOptionItem>;
      setDrugTypes(drugTypesOptions);
      const drugDataTypeOptions = getOffenseOptionItems(
        'dataSource',
        filteredOptions,
      ) as Map<string, IOffenseOptionItem>;
      setDrugDataSources(drugDataTypeOptions);

      const involvementOptions = getOffenseOptionItems(
        'involvement',
        filteredOptions,
      ) as Map<string, IOffenseOptionItem>;
      setPropertyInvolvementList(involvementOptions);
    }
  }, [filteredOptions]);

  return (
    PropertyInvolvementList &&
    DrugType &&
    drugDataSources &&
    PropertyType &&
    filteredOptions && (
      <KaseMainContext.Provider
        value={{
          PropertyInvolvementList,
          DrugType,
          drugDataSources,
          PropertyType,
          filteredOptions,
          refetchCounts,
        }}
      >
        {children}
      </KaseMainContext.Provider>
    )
  );
}
